import FileUpload from "./FileUpload";

/**
 * Lets admin pick how to source a video:
 *   - direct: MP4/M3U8 URL (text input)
 *   - upload: file upload to object storage
 *   - embed_url: a player URL like https://killerplayer.com/watch/video/<id> (rendered as iframe)
 *   - embed_code: a raw <iframe> HTML snippet (rendered as-is)
 *
 * Props:
 *   source:     current videoSource string
 *   videoUrl:   current videoUrl (used by direct/upload/embed_url)
 *   embedCode:  current embedCode (used by embed_code)
 *   onChange({videoSource, videoUrl, embedCode})
 */
export default function VideoSourceSelector({ source = "direct", videoUrl = "", embedCode = "", onChange }) {
  const set = (patch) => onChange({ videoSource: source, videoUrl, embedCode, ...patch });

  const opts = [
    { v: "direct", label: "Direct URL (MP4 / M3U8)" },
    { v: "upload", label: "Upload video file" },
    { v: "embed_url", label: "Embed player URL (iframe)" },
    { v: "embed_code", label: "Embed HTML code (raw)" },
  ];

  return (
    <div data-testid="video-source-selector">
      <label className="text-xs uppercase tracking-wider text-[#888] block mb-1.5">Video source</label>
      <div className="grid grid-cols-2 gap-2 mb-3">
        {opts.map((o) => (
          <button
            key={o.v}
            type="button"
            onClick={() => set({ videoSource: o.v })}
            className={`text-xs py-2 px-3 rounded-md border text-left transition ${source === o.v ? "border-[#E50914] bg-[#E50914]/10 text-white" : "border-white/10 bg-[#0d0d0d] text-[#aaa] hover:border-white/30"}`}
            data-testid={`video-source-${o.v}`}
          >
            {o.label}
          </button>
        ))}
      </div>

      {source === "direct" && (
        <input
          type="text"
          value={videoUrl}
          onChange={(e) => set({ videoUrl: e.target.value })}
          placeholder="https://example.com/video.mp4 or .m3u8"
          className="w-full px-3 py-2 bg-[#1a1a1a] border border-white/10 rounded-md text-sm focus:outline-none focus:border-[#E50914]"
          data-testid="video-direct-input"
        />
      )}
      {source === "upload" && (
        <FileUpload
          value={videoUrl}
          onChange={(url) => set({ videoUrl: url })}
          accept="video/*"
          kind="video"
        />
      )}
      {source === "embed_url" && (
        <input
          type="text"
          value={videoUrl}
          onChange={(e) => set({ videoUrl: e.target.value })}
          placeholder="https://killerplayer.com/watch/video/<id>"
          className="w-full px-3 py-2 bg-[#1a1a1a] border border-white/10 rounded-md text-sm focus:outline-none focus:border-[#E50914]"
          data-testid="video-embed-url-input"
        />
      )}
      {source === "embed_code" && (
        <textarea
          rows="4"
          value={embedCode}
          onChange={(e) => set({ embedCode: e.target.value })}
          placeholder='<iframe src="..." allowfullscreen></iframe>'
          className="w-full px-3 py-2 bg-[#1a1a1a] border border-white/10 rounded-md text-sm font-mono focus:outline-none focus:border-[#E50914]"
          data-testid="video-embed-code-input"
        />
      )}
    </div>
  );
}
